import { useEffect, useState } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { routes } from '@/utils/routes'
import type { ContactAnchorType } from '@/api/contactDetail'

export type ContactTabKey = 'overview' | 'quiz' | 'activity' | 'emails' | 'profile'

const TABS: ContactTabKey[] = ['overview', 'quiz', 'activity', 'emails', 'profile']

interface UseContactTabRouterParams {
  clientCompanyId: string
  workspaceId: string
  /** Absent => cross-campaign (workspace) scope. */
  campaignId?: string
  webId?: string
  anchorType: ContactAnchorType
  anchorId: string
}

// Mirrors useWebTabRouter: derives the active tab from the trailing path segment and routes between tabs
// using the scoped contact-route helpers. Overview is the default (URL has no trailing tab segment).
export function useContactTabRouter({ clientCompanyId, workspaceId, campaignId, webId, anchorType, anchorId }: UseContactTabRouterParams): {
  activeTab: ContactTabKey
  routeToTab: (tab: ContactTabKey) => void
} {
  const location = useLocation()
  const [activeTab, setActiveTab] = useState<ContactTabKey>('overview')

  useEffect(() => {
    const rawPath = (location as unknown as { path?: string; url?: string })?.path
      || (location as unknown as { path?: string; url?: string })?.url
      || (typeof window !== 'undefined' ? window.location.pathname : '')
    const last = rawPath.split('?')[0].split('/').filter(Boolean).pop() || ''
    const matched = (TABS as string[]).includes(last) ? (last as ContactTabKey) : 'overview'
    if (matched !== activeTab) setActiveTab(matched)
  }, [location])

  const scoped = routes.scoped(clientCompanyId, workspaceId)
  const buildPath = (tab?: string) => {
    if (!campaignId) return scoped.workspaceContact(anchorType, anchorId, tab)
    return webId
      ? scoped.webContact(campaignId, webId, anchorType, anchorId, tab)
      : scoped.campaignContact(campaignId, anchorType, anchorId, tab)
  }

  const routeToTab = (tab: ContactTabKey) => {
    setActiveTab(tab)
    const target = tab === 'overview' ? buildPath() : buildPath(tab)
    try { location.route(target) } catch { if (typeof window !== 'undefined') window.history.pushState({}, '', target) }
  }

  return { activeTab, routeToTab }
}
